import { Injectable } from '@angular/core';
import { QuoteHelper } from './quote.helper';
import { GWCreateInsuredEventRequest } from '../models/agents-gateway/save-quote/gw-create-insured-event-request.model';
import { GWInsuredEvent } from '../models/agents-gateway/save-quote/gw-insured-event.model';
import { IApiAcknowledgement, IApiTraveler, DateInformation, ApiQuoteResponse, ApiCoverageGroups, ApiCoverage, ApiCoverageDetail, LocalStorage } from '@bhtp-client-api/base';
import { GWBaseQuote } from '../models/agents-gateway/save-quote/gw-base-quote.model';
import { Flight, PurchaseData, Traveler, Country, Coverage } from '..';
import { GWAcknowledgement } from '../models/agents-gateway/save-quote/gw-acknowledgement.model';
import { GWInsured } from '../models/agents-gateway/save-quote/gw-insured.model';
import moment = require('moment');
import { DateFormats } from '../date-formats';
import { GWCarRental } from '../models/agents-gateway/save-quote/gw-car-rental.model';
import { GWDestination } from '../models/agents-gateway/save-quote/gw-destination.model';
import { valueAsCents, centsAsDollars } from './curency.util';
import { GWFlight } from '../models/agents-gateway/save-quote/gw-flight.model';
import { GWAttribute } from '../models/agents-gateway/save-quote/gw-attribute.model';
import { GWCoverage } from '../models/agents-gateway/save-quote/gw-coverage.model';
import { CoverageService } from '../../coverage-modal/coverage.service';


@Injectable()
export class InsuredEventMapperV2 {

    private readonly sortOrderField: string = 'sortOrder';
    private readonly quoteDbDiField: string = 'id';

    constructor(private quoteHelper: QuoteHelper, private localStorage: LocalStorage, private coverageService: CoverageService) { }

    public mapPurchaseDataAndQuoteToInsuredEvent(data: PurchaseData, quotes: ApiQuoteResponse[]): GWInsuredEvent {
        if (!data || !data.primaryTraveler) {
            throw new Error('There must be at least one traveler on the trip');
        }

        const primaryTraveler: Traveler = data.primaryTraveler;
        if (!primaryTraveler || !primaryTraveler.emailGroup || !primaryTraveler.emailGroup.email) {
            throw new Error('There must be an email address on the primary traveler');
        }

        const event: GWInsuredEvent = new GWInsuredEvent();
        event.attributes = [];
        event.id = data.eventId;

        if (data.agencyCode && data.agencyCode) {
            event.agentCode = data.agentCode || null;
            event.agencyCode = data.agencyCode || null;
        } else {
            const profile = JSON.parse(localStorage.getItem('profile'));
            event.agentCode = profile.AgentCode || 'BHTP';
            event.agencyCode = profile.AgencyCode || 'BHTP';
        }

        event.externalCustomerId = this.tryGetUserId(data);

        if (primaryTraveler.address) {
            event.addressLine1 = primaryTraveler.address.address1 || null;
            event.addressLine2 = primaryTraveler.address.address2 || null;
            event.city = primaryTraveler.address.city || null;
            event.stateIso2 = primaryTraveler.address.stateOrProvince || null;
            event.postalCode = primaryTraveler.address.postalCode || null;
            event.countryIso2 = 'US' // TODO This will have to be fixed when we start accepting other countries;
        }

        // Take the start date from one of the quotes, at this point they are all the same
        const [startDate, startDateTimezone] = this.safeGetDateDetails(data.departureDate, quotes[0].departureDates);
        event.startDate = startDate;
        event.startDateTimezone = startDateTimezone;

        // Take the end date from one of the quotes, at this point they are all the same
        const [endDate, endDateTimezone] = this.safeGetDateDetails(data.returnDate, quotes[0].returnDates);
        event.endDate = endDate;
        event.endDateTimezone = endDateTimezone;

        event.initialDepositDate = data.depositDate || null;

        event.totalInsurableCost = valueAsCents(this.calculateTotalInsurableCost([...data.additionalTravelers, data.primaryTraveler]));

        event.insureds = this.mapTravelersToInsured([...data.additionalTravelers, data.primaryTraveler]);

        if (data.flights && data.flights.length > 0) {
            event.flights = this.mapFlights(data.flights);
            event.attributes.push(new GWAttribute('flightSegments', data.flights.length));
        }

        // Take the flight connection count one of the quotes, at this point they are all the same
        if (quotes[0] && quotes[0].flightConnectionCount != null && quotes[0].flightConnectionCount != undefined) {
            event.attributes.push(new GWAttribute('flightConnectionCount', quotes[0].flightConnectionCount));
        }

        event.carRentals = this.mapCarRental(data.rentalCarPickupDate, data.rentalCarReturnDate, data.carRentalCompany);

        event.destinations = this.mapDestination(data.destination, data.destinationCountry, data.destinationState, data.destinationCity);

        if (data.airline) {
            event.attributes.push(new GWAttribute('airline', data.airline));
        }
        if (data.tourOperator) {
            event.attributes.push(new GWAttribute('tourOperator', data.tourOperator));
        }
        if (data.carRentalCompany) {
            event.attributes.push(new GWAttribute('carRentalCompany', data.carRentalCompany));
        }
        if (data.cruiseLine) {
            event.attributes.push(new GWAttribute('cruiseLine', data.cruiseLine));
        }
        event.attributes.push(new GWAttribute('submissionChannel', 'agents'));

        event.quotes = this.mapQuotes(data, quotes);

        return event;
    }

    private tryGetUserId(data: any): string {
        return data.customerId ? data.customerId : undefined;
    }

    private mapQuotes(data: PurchaseData, quotes: ApiQuoteResponse[]): GWBaseQuote[] {
        const mappedQuotes: GWBaseQuote[] = [];
        const coverageService: CoverageService = this.coverageService;

        for (let i: number = 0; i <= quotes.length - 1; i++) {
            const mappedQuote: GWBaseQuote = new GWBaseQuote();

            if (data.quoteNumber && !isNaN(parseInt(data.quoteNumber))) {
                mappedQuote.id = parseInt(data.quoteNumber)
            } else if (quotes[i][this.quoteDbDiField] && !isNaN(parseInt(quotes[i][this.quoteDbDiField]))) {
                mappedQuote.id = parseInt(quotes[i][this.quoteDbDiField])
            }

            mappedQuote.eventId = data.eventId;
            mappedQuote.ratingEngineId = quotes[i].quoteId;
            // Take the rating id from the quotes (quick quote) or the event (purchase path)
            mappedQuote.planId = quotes[i].package ? quotes[i].package.ratingId : data.ratingId;
            mappedQuote.planName = quotes[i].packageName || null;
            mappedQuote.planPriority = quotes[i].package && quotes[i].package[this.sortOrderField] ? quotes[i].package[this.sortOrderField] : null
            mappedQuote.coverages = mapCoverages(quotes[i].coverages);
            mappedQuote.totalPremium = valueAsCents(this.quoteHelper.getTotalPriceForQuote(quotes[i]));
            mappedQuote.basePremium = valueAsCents(quotes[i].baseQuoteAmount);
            mappedQuote.totalFees = quotes[i].totalFees ? valueAsCents(quotes[i].totalFees) : 0;
            mappedQuote.acknowledgements = mapAcknowledgements(data.acknowledgements);
            mappedQuotes.push(mappedQuote);
        }

        return mappedQuotes;

        function mapCoverages(coverages: ApiCoverageGroups): GWCoverage[] {
            let coveragesToMap: ApiCoverageDetail[] = coverages.included;

            // Reconcile selected Coverages
            if (data && data.selectedCoverages && data.selectedCoverages.length > 0) {
                data.selectedCoverages.forEach( (c) => {
                    const foundCoverage = coverages.optional.find( (fc) => fc.ratingId == c.ratingId);
                    if(foundCoverage) {
                        foundCoverage.isSelected = true;
                        foundCoverage.coverageLimit = c.coverageLimit;
                        foundCoverage.premium = coverageService.getOptionalCoveragePremium(foundCoverage);
                    }
                })
            }

            if (coverages.bonus) {
                coveragesToMap = coveragesToMap.concat(coverages.bonus);
            }

            if (coverages.optional) {
                coveragesToMap = coveragesToMap.concat(coverages.optional.filter(coverage => coverage && coverage.isSelected));
            }

            return coveragesToMap
                .filter(coverage => coverage)
                .map(coverage => {
                    const mappedCoverage: GWCoverage = new GWCoverage();

                    mappedCoverage.externalRatingId = coverage.ratingId || null;
                    mappedCoverage.type = coverage.type || null;
                    mappedCoverage.premium = valueAsCents(coverage.premium);
                    mappedCoverage.selectedLimit = valueAsCents(coverage.coverageLimit);

                    return mappedCoverage;
                });
        }

        function mapAcknowledgements(acknowledgements: IApiAcknowledgement[]): GWAcknowledgement[] {
            return acknowledgements ?
                acknowledgements
                    .filter(ack => ack)
                    .map((ack) => {
                        const mappedAck: GWAcknowledgement = new GWAcknowledgement();

                        mappedAck.type = ack.type || null;
                        mappedAck.typeIdentifier = ack.typeIdentifier || null;
                        mappedAck.relatedType = ack.relatedType || null;
                        mappedAck.relatedTypeIdentifier = ack.relatedTypeIdentifier || null;
                        mappedAck.message = ack.message || null;
                        mappedAck.coverageRatingIds = ack.coverages;

                        return mappedAck;
                    }) : null;
        }
    }

    private mapTravelersToInsured(travelers: Traveler[]): GWInsured[] {
        return travelers.map((traveler) => {
            const insured: GWInsured = new GWInsured();

            insured.firstName = traveler.firstName || null;
            insured.lastName = traveler.lastName || null;
            insured.isPrimary = traveler.isPrimary || false;
            insured.dateOfBirth = traveler.birthDate || null;
            insured.insurableCost = valueAsCents(traveler.tripCost);
            if (traveler && traveler.emailGroup && traveler.emailGroup.email) {
                insured.email = traveler.emailGroup.email
            } else {
                insured.email = null;
            }
            insured.phone = traveler.phoneNumber || null;

            if (traveler.address) {
                insured.addressLine1 = traveler.address.address1 || null;
                insured.addressLine2 = traveler.address.address2 || null;
                insured.city = traveler.address.city || null;
                insured.stateIso2 = traveler.address.stateOrProvince || null;
                insured.postalCode = traveler.address.postalCode || null;
                insured.countryIso2 = "US";  // TODO needs updating when we start collecting Country
            }

            return insured;
        });
    }

    private mapFlights(flights: Flight[]): GWFlight[] {
        return flights ? flights
        .filter(flight => flight && !Flight.isEmpty(flight))
            .map((flight) => {
                const mappedflight: GWFlight = new GWFlight();

                mappedflight.airlineIataCode = flight.airline.providerCode || null;
                mappedflight.airlineName = flight.airline.name || null;
                mappedflight.flightNumber = flight.flightNumber || null;
                mappedflight.departureDate = flight.flightDate ?
                    moment(flight.flightDate, DateFormats.IsoCalendarDateForStorage)
                        .format(DateFormats.IsoCalendarDateForStorage) :
                    null;
                mappedflight.departureAirportIataCode = flight.departureAirport || null;
                mappedflight.arrivalAirportIataCode = flight.arrivalAirport || null;

                return mappedflight;
            }) : null;
    }

    private safeGetDateDetails(standAloneDate: string, dateInformation: DateInformation): [string, string] {
        let fullDateTime: string, timezone: string = null;

        if (dateInformation) {
            fullDateTime = dateInformation.localized && dateInformation.localized.dateString ? dateInformation.localized.dateString : null;
            timezone = dateInformation.localOlsonTimeZone || null;
        } else if (standAloneDate) {
            fullDateTime = standAloneDate;
        }

        return [ fullDateTime, timezone ];
    }

    private calculateTotalInsurableCost(travelers: Traveler[]): number {
        return travelers
            .filter(traveler => traveler && !isNaN(traveler.tripCost))
            .reduce((totalTripcost, currentTraveler) => totalTripcost += currentTraveler.tripCost, 0);
    }

    private mapCarRental(pickupDate: string, returnDate: string, provider: string): GWCarRental[] {
        if (pickupDate && returnDate && provider) {
            const carRental: GWCarRental = new GWCarRental();

            carRental.startDate = pickupDate || null;
            carRental.endDate = returnDate || null;
            carRental.provider = provider || null;

            return [carRental];
        }

        return null;
    }

    private mapDestination(destination: string, destinationCountry: Country, destinationState: string, destinationCity: string): GWDestination[] {
        const mappedDestination: GWDestination = new GWDestination();
        if (destinationCountry) {
            mappedDestination.countryIso2 = destinationCountry.isoCode2 || null;
            mappedDestination.destination = destinationCountry.name || null;
        } else {
            mappedDestination.destination = destination;
        }

        mappedDestination.city = destinationCity
        mappedDestination.stateIso2 = destinationState

        return [mappedDestination];
    }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./src/typescript/shared/helpers/insured-event.mapper-v2.ts